import React, { useEffect, useState } from "react";
import { UserPlus, Mail, Send, Clock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { timeAgo } from "@/components/PostCard";

export function InviteFamily() {
  const [invites, setInvites] = useState([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => { try { const r = await api.get("/invites"); setInvites(r.data); } catch {} };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/invites", { name, email, message });
      toast.success(`Invitation sent to ${name}!`);
      setName(""); setEmail(""); setMessage("");
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border-2 border-border shadow-sm p-6 sm:p-8" data-testid="invite-family">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-12 h-12 rounded-xl bg-gold-light border-2 border-gold flex items-center justify-center shrink-0">
          <UserPlus size={26} strokeWidth={2.5} className="text-terracotta" />
        </div>
        <div>
          <h2 className="font-head text-2xl font-bold text-forest-900">Invite family & friends</h2>
          <p className="text-forest-500">Ask a loved one to join so you can meet up and stay close.</p>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-4 mt-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="font-bold text-forest-900 mb-1.5 block">Their name</label>
            <input data-testid="invite-name" required value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Sarah" className="w-full min-h-[56px] text-lg p-4 rounded-xl border-2 border-border focus:border-terracotta focus:ring-4 focus:ring-terracotta/20 outline-none bg-cream" />
          </div>
          <div>
            <label className="font-bold text-forest-900 mb-1.5 block">Their email</label>
            <div className="relative">
              <Mail size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-forest-500" strokeWidth={2.5} />
              <input data-testid="invite-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="sarah@example.com" className="w-full min-h-[56px] text-lg pl-12 pr-4 rounded-xl border-2 border-border focus:border-terracotta focus:ring-4 focus:ring-terracotta/20 outline-none bg-cream" />
            </div>
          </div>
        </div>
        <div>
          <label className="font-bold text-forest-900 mb-1.5 block">Personal note (optional)</label>
          <textarea data-testid="invite-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={2}
            placeholder="Add a warm message to your invitation" className="w-full text-lg p-4 rounded-xl border-2 border-border focus:border-terracotta outline-none bg-cream resize-none" />
        </div>
        <button type="submit" disabled={busy} data-testid="invite-submit"
          className="w-full min-h-[56px] rounded-full bg-terracotta text-white font-bold text-lg hover:bg-terracotta-dark transition-transform active:scale-95 disabled:opacity-60 flex items-center justify-center gap-2">
          <Send size={22} strokeWidth={2.5} /> {busy ? "Sending…" : "Send invitation"}
        </button>
      </form>

      {invites.length > 0 && (
        <div className="mt-6 pt-6 border-t border-border">
          <h3 className="font-bold text-forest-900 mb-3">People you've invited</h3>
          <div className="space-y-2" data-testid="invite-list">
            {invites.map((inv) => (
              <div key={inv.invite_id} className="flex items-center gap-3 p-3 rounded-xl bg-cream border border-border">
                <div className="min-w-0">
                  <p className="font-semibold text-forest-900 truncate">{inv.name} <span className="text-forest-500 font-normal">· {inv.email}</span></p>
                  <p className="text-xs text-forest-500">{timeAgo(inv.created_at)}</p>
                </div>
                {inv.status === "joined" ? (
                  <span className="ml-auto shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-forest-700 text-white text-sm font-bold"><CheckCircle2 size={16} strokeWidth={2.5} /> Joined</span>
                ) : (
                  <span className="ml-auto shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gold text-forest-900 text-sm font-bold"><Clock size={16} strokeWidth={2.5} /> Pending</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
